// Builds the downloadable PDF sizing report. Mirrors the on-screen output panel
// section for section (same values, labels, formatting and platform callout) so
// the two never disagree.
import type { jsPDF as JsPDF } from "jspdf";
import {
  USE_CASES,
  MODEL_FAMILIES,
  type CalculationResult,
} from "@/types/calculator";
import { approxTime, lowConfidenceNote } from "@/lib/latency";
import { SLICE_COLORS, buildVramSummary, drawDonutPng, fmtGb, hexToRgb } from "@/lib/vramBreakdown";

type RGB = [number, number, number];

// Brand tokens from index.css (--lenovo-red / --lenovo-blue / --lenovo-gray).
const RED: RGB = [240, 36, 36];
const BLUE: RGB = [6, 69, 121];
const GRAY: RGB = [110, 117, 124];
const INK: RGB = [33, 37, 41];
const MUTED: RGB = [96, 102, 108];
const RULE: RGB = [222, 226, 230];
const PANEL: RGB = [248, 249, 250];

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 16;
const CONTENT_W = PAGE_W - MARGIN * 2;
const CONTENT_BOTTOM = PAGE_H - 20; // keep clear of the footer
const LINE = 4.4; // body line height (mm) at 9pt

const DISCLAIMER =
  "These values are approximate estimates based on the provided inputs and may not " +
  "reflect exact performance. Validate against benchmarks on the target hardware " +
  "before procurement.";

export function reportFileName(date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `llm-sizing-report-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.pdf`;
}

export function buildSizingReport(JsPDFCtor: typeof JsPDF, result: CalculationResult): JsPDF {
  const doc = new JsPDFCtor({ orientation: "portrait", unit: "mm", format: "a4" });
  const generated = new Date();
  let y = 0;

  const color = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
  const fill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
  const stroke = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
  const font = (style: "normal" | "bold", size: number) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
  };
  const wrap = (text: string, width: number): string[] => doc.splitTextToSize(text, width);

  const ensure = (h: number) => {
    if (y + h > CONTENT_BOTTOM) {
      doc.addPage();
      y = 20;
    }
  };

  // `keep` reserves room for the block that follows so a short section is not
  // split across pages.
  const sectionTitle = (title: string, keep = 18) => {
    ensure(keep + 8);
    font("bold", 11);
    color(BLUE);
    doc.text(title.toUpperCase(), MARGIN, y);
    stroke(RULE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, y + 2.2, MARGIN + CONTENT_W, y + 2.2);
    y += 8;
  };

  // Label/value table rows with hairline separators; long values wrap.
  const kvRows = (rows: [string, string, boolean?][], x = MARGIN, width = CONTENT_W, labelW = 62) => {
    const startY = y;
    for (const [label, value, emphasis] of rows) {
      font(emphasis ? "bold" : "normal", 9);
      const lines = wrap(value, width - labelW - 2);
      const h = Math.max(1, lines.length) * LINE + 2.6;
      ensure(h);
      font("normal", 9);
      color(MUTED);
      doc.text(label, x + 1, y + 4);
      font(emphasis ? "bold" : "normal", 9);
      color(emphasis ? RED : INK);
      doc.text(lines, x + labelW, y + 4);
      y += h;
      stroke(RULE);
      doc.setLineWidth(0.2);
      doc.line(x, y, x + width, y);
    }
    return y - startY;
  };

  // Two-column label/value grid (Input Summary). `wide` rows span both columns.
  const grid = (wide: [string, string][], items: [string, string][]) => {
    const colW = CONTENT_W / 2;
    const labelW = 34;
    for (const [label, value] of wide) {
      font("bold", 9);
      const lines = wrap(value, CONTENT_W - labelW - 4);
      const h = lines.length * LINE + 2.6;
      ensure(h);
      font("normal", 9);
      color(MUTED);
      doc.text(label, MARGIN + 1, y + 4);
      font("bold", 9);
      color(INK);
      doc.text(lines, MARGIN + labelW, y + 4);
      y += h;
      stroke(RULE);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    }
    for (let i = 0; i < items.length; i += 2) {
      const pair = items.slice(i, i + 2);
      font("bold", 9);
      const wrapped = pair.map(([, v]) => wrap(v, colW - labelW - 4));
      const h = Math.max(...wrapped.map((l) => l.length)) * LINE + 2.6;
      ensure(h);
      pair.forEach(([label], j) => {
        const cx = MARGIN + j * colW;
        font("normal", 9);
        color(MUTED);
        doc.text(label, cx + 1, y + 4);
        font("bold", 9);
        color(INK);
        doc.text(wrapped[j], cx + labelW, y + 4);
      });
      y += h;
      stroke(RULE);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    }
  };

  const inp = result.inputSummary;
  const gpu = result.recommendedConfig;
  const srv = result.serverConfiguration;
  const perf = result.otherParameters;

  const modelLabel =
    inp.modelFamily === "custom"
      ? inp.customModelName || "Custom"
      : (MODEL_FAMILIES as Record<string, string>)[inp.modelFamily] ?? inp.modelFamily;
  const useCaseLabel = (USE_CASES as Record<string, string>)[inp.useCase] ?? inp.useCase;
  const e2e = approxTime(perf.estimated_response_time, "sec");
  const pp = result.pipelineParallelism ?? 1;
  const copies = result.vramBreakdown.copies ?? 1;
  const parallelism = `TP = ${result.tensorParallelism ?? gpu.quantity}${pp > 1 ? ` · PP = ${pp}` : ""}${copies > 1 ? ` · ${copies} copies` : ""}`;

  // ---------------- Header ----------------
  fill(RED);
  doc.rect(0, 0, PAGE_W, 28, "F");
  color([255, 255, 255]);
  font("bold", 17);
  doc.text("LLM Sizing Report", MARGIN, 13);
  font("normal", 9);
  doc.text("Lenovo LLM Sizing Calculator  ·  GPU & infrastructure recommendation", MARGIN, 20);
  doc.text(
    generated.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }),
    PAGE_W - MARGIN, 13, { align: "right" },
  );
  doc.text(
    generated.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
    PAGE_W - MARGIN, 20, { align: "right" },
  );
  y = 38;

  // ---------------- Executive summary ----------------
  {
    font("normal", 9);
    const subtitle = `${modelLabel}  ·  ${useCaseLabel}  ·  ${inp.concurrentUsers} concurrent users`;
    const subLines = wrap(subtitle, CONTENT_W - 12);
    const platformLine = srv.platform ? `${srv.compute}  ·  ${srv.platform}` : srv.compute;
    const platLines = wrap(platformLine, CONTENT_W - 12);
    const tilesH = 16;
    const boxH = 12 + 8 + subLines.length * LINE + platLines.length * LINE + 4 + tilesH + 6;
    ensure(boxH);
    fill(PANEL);
    stroke(RULE);
    doc.setLineWidth(0.3);
    doc.rect(MARGIN, y, CONTENT_W, boxH, "FD");
    fill(RED);
    doc.rect(MARGIN, y, 1.6, boxH, "F");

    let by = y + 7;
    font("bold", 8);
    color(GRAY);
    doc.text("RECOMMENDED DEPLOYMENT", MARGIN + 6, by);
    by += 7;
    font("bold", 15);
    color(INK);
    doc.text(`${gpu.quantity}x ${gpu.name}`, MARGIN + 6, by);
    by += 6;
    font("normal", 9);
    color(MUTED);
    doc.text(subLines, MARGIN + 6, by);
    by += subLines.length * LINE;
    color(INK);
    doc.text(platLines, MARGIN + 6, by);
    by += platLines.length * LINE + 2;

    const tiles: [string, string][] = [
      ["Total VRAM", `${gpu.totalVram} GB`],
      ["Parallelism", parallelism],
      ["Request latency (E2E)", e2e],
      ["Max concurrent users", String(perf.max_conc_users)],
    ];
    const tileW = (CONTENT_W - 12 - 3 * 3) / 4;
    tiles.forEach(([label, value], i) => {
      const tx = MARGIN + 6 + i * (tileW + 3);
      fill([255, 255, 255]);
      stroke(RULE);
      doc.rect(tx, by, tileW, tilesH, "FD");
      font("normal", 7.5);
      color(MUTED);
      doc.text(label, tx + 3, by + 5.5);
      font("bold", 11);
      color(i === 0 ? RED : INK);
      doc.text(value, tx + 3, by + 12);
    });
    y += boxH + 8;
  }

  // ---------------- Warning (matches the red banner in the UI) ----------------
  if (result.warning) {
    font("normal", 9);
    const lines = wrap(result.warning, CONTENT_W - 10);
    const boxH = 10 + lines.length * LINE;
    ensure(boxH + 4);
    doc.setFillColor(254, 242, 242);
    doc.setDrawColor(252, 165, 165);
    doc.rect(MARGIN, y, CONTENT_W, boxH, "FD");
    doc.setTextColor(153, 27, 27);
    font("bold", 9);
    doc.text("Warning", MARGIN + 5, y + 6);
    font("normal", 9);
    doc.text(lines, MARGIN + 5, y + 10.5);
    y += boxH + 8;
  }

  // ---------------- Input Summary ----------------
  sectionTitle("Input Summary");
  grid([
    ["Model", modelLabel],
  ], [
    ["Use Case", useCaseLabel],
    ["Precision", String(inp.precision).toUpperCase()],
    ["RAG Enabled", inp.enableRAG ? "Yes" : "No"],
    ["High Availability", inp.highAvailability ? "Yes" : "No"],
    ["Concurrent Users", String(inp.concurrentUsers)],
    ["Tokens (in / out)", `${inp.inputTokens} / ${inp.outputTokens}`],
    ["Requests / user / min", String(inp.requestsPerUserPerMinute ?? "—")],
    ["GPU Overhead", `${inp.gpuOverhead}%`],
    ["Parallelism", parallelism],
  ]);
  y += 8;

  // ---------------- Recommended GPU Configuration ----------------
  sectionTitle("Recommended GPU Configuration", 32);
  kvRows([
    ["GPU", gpu.name],
    ["Quantity", `${gpu.quantity} ${gpu.quantity === 1 ? "GPU" : "GPUs"}`],
    ["VRAM per GPU", `${gpu.vramPerGpu} GB`],
    ["Total VRAM", `${gpu.totalVram} GB`, true],
  ]);
  y += 6;

  // GPU memory breakdown: donut of installed VRAM plus a table explaining each
  // slice. Same data, colours and wording as the on-screen chart
  // (vram-breakdown-chart.tsx).
  {
    const vs = buildVramSummary(result);
    ensure(24);
    font("bold", 9.5);
    color(INK);
    doc.text("GPU memory breakdown", MARGIN + 1, y + 3);
    font("normal", 8.5);
    color(MUTED);
    doc.text(`${vs.gpuCount} x ${vs.vramPerGpu} GB GPUs = ${fmtGb(vs.totalGb)} installed`, MARGIN + CONTENT_W, y + 3, { align: "right" });
    y += 8;
    font("normal", 9);
    color(INK);
    const free = vs.slices.find((x) => x.key === "free");
    const usedPct = vs.totalGb > 0 ? Math.round((vs.usedGb / vs.totalGb) * 100) : 0;
    doc.text(
      vs.shortfallGb > 0
        ? `The workload needs ${fmtGb(vs.usedGb)}, but only ${fmtGb(vs.totalGb)} is installed  ·  short by ${fmtGb(vs.shortfallGb)}`
        : `The workload uses ${fmtGb(vs.usedGb)} of the ${fmtGb(vs.totalGb)} installed (${usedPct}%)` +
            (free ? `  ·  ${fmtGb(free.gb)} free headroom` : ""),
      MARGIN + 1, y,
    );
    y += 4;

    // Donut on the left, table on the right (same layout as the screen).
    const pie = 46;
    const tx = MARGIN + pie + 8;
    const tw = CONTENT_W - pie - 8;
    font("normal", 7.5);
    const rows = vs.slices.map((slice) => ({ slice, detail: wrap(slice.detail, tw - 34) }));
    const tableH = rows.reduce((h, r) => h + 4.6 + r.detail.length * 3.4 + 1.6, 0) + 7;
    ensure(Math.max(pie, tableH) + 4);
    const top = y;

    const png = drawDonutPng(vs);
    if (png) doc.addImage(png, "PNG", MARGIN, top + Math.max(0, (tableH - pie) / 2), pie, pie);
    const cy = top + Math.max(0, (tableH - pie) / 2) + pie / 2;
    font("bold", 13);
    color(INK);
    doc.text(`${usedPct}%`, MARGIN + pie / 2, cy + 0.5, { align: "center" });
    font("normal", 6.5);
    color(MUTED);
    doc.text(vs.shortfallGb > 0 ? "of installed" : "in use", MARGIN + pie / 2, cy + 4, { align: "center" });

    for (const { slice, detail } of rows) {
      const h = 4.6 + detail.length * 3.4 + 1.6;
      const [r, g, b] = hexToRgb(SLICE_COLORS[slice.key].light);
      doc.setFillColor(r, g, b);
      doc.rect(tx + 1, y + 1.4, 3, 3, "F");
      font("bold", 8.5);
      color(INK);
      doc.text(slice.label, tx + 6, y + 4);
      doc.text(fmtGb(slice.gb), tx + tw - 14, y + 4, { align: "right" });
      font("normal", 8);
      color(MUTED);
      doc.text(`${slice.pct.toFixed(1)}%`, tx + tw - 1, y + 4, { align: "right" });
      font("normal", 7.5);
      doc.text(detail, tx + 6, y + 7.6);
      y += h;
      stroke(RULE);
      doc.setLineWidth(0.2);
      doc.line(tx, y, tx + tw, y);
    }
    font("bold", 8.5);
    color(INK);
    doc.text("Installed VRAM", tx + 6, y + 4.4);
    doc.text(fmtGb(vs.totalGb), tx + tw - 14, y + 4.4, { align: "right" });
    font("normal", 8);
    color(MUTED);
    doc.text("100%", tx + tw - 1, y + 4.4, { align: "right" });
    y = Math.max(top + pie, y + 7) + 10;
  }

  // ---------------- Server Configuration ----------------
  sectionTitle("Server Configuration", srv.platform ? 80 : 40);
  kvRows([
    ["Recommended Server Type", srv.category, true],
    ["Compute", srv.compute],
    ["Storage", srv.storage],
    ["Network", srv.network],
    ["Switching", srv.networking],
    ...(srv.rsus
      ? [["Scalable units", `${srv.rsus} (up to 4 compute nodes each)${srv.gpusPerNode ? `, ${srv.gpusPerNode} GPUs per node` : ""}`] as [string, string]]
      : []),
  ]);

  // Platform callout: same content and wording as the on-screen callout, and
  // likewise omitted when the backend recommends no platform.
  if (srv.platform) {
    const innerW = CONTENT_W - 12;
    font("normal", 8.5);
    const specLines = wrap([srv.platform_spec, srv.platform_servers].filter(Boolean).join("  ·  "), innerW);
    const bestLines = srv.platform_best_for ? wrap(`Best for ${srv.platform_best_for}.`, innerW) : [];
    font("normal", 8);
    const whyLines = srv.platform_rationale
      ? wrap(`Sized as a ${srv.platform_rationale} deployment.`, innerW)
      : [];
    const boxH = 11 + (specLines.length + bestLines.length) * 4.2 + whyLines.length * 4 + 4;
    y += 5;
    ensure(boxH);
    doc.setFillColor(254, 242, 242);
    doc.rect(MARGIN, y, CONTENT_W, boxH, "F");
    fill(RED);
    doc.rect(MARGIN, y, 1.6, boxH, "F");

    let cy = y + 7;
    font("bold", 10);
    color(INK);
    doc.text(`Recommended platform: ${srv.platform}${srv.platform_tier ? `  (${srv.platform_tier})` : ""}`, MARGIN + 6, cy);
    if (srv.platform_url) {
      font("normal", 8.5);
      color(BLUE);
      const label = "Platform guide";
      const lw = doc.getTextWidth(label);
      doc.textWithLink(label, MARGIN + CONTENT_W - 4 - lw, cy, { url: srv.platform_url });
      doc.setDrawColor(BLUE[0], BLUE[1], BLUE[2]);
      doc.setLineWidth(0.2);
      doc.line(MARGIN + CONTENT_W - 4 - lw, cy + 0.8, MARGIN + CONTENT_W - 4, cy + 0.8);
    }
    cy += 5.5;
    font("normal", 8.5);
    color(MUTED);
    if (specLines.length) {
      doc.text(specLines, MARGIN + 6, cy);
      cy += specLines.length * 4.2;
    }
    if (bestLines.length) {
      doc.text(bestLines, MARGIN + 6, cy);
      cy += bestLines.length * 4.2;
    }
    if (whyLines.length) {
      font("normal", 8);
      color(GRAY);
      doc.text(whyLines, MARGIN + 6, cy + 0.5);
    }
    y += boxH;
  }
  y += 8;

  // ---------------- Solution Power (Lenovo sizing rubric) ----------------
  if (srv.power) {
    const pw = srv.power;
    const kw = (w: number) => `${(w / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} kW`;
    sectionTitle("Solution Power", 30 + pw.items.length * 9);
    font("bold", 11);
    color(INK);
    doc.text(`${pw.peak_kw.toLocaleString()} kW peak`, MARGIN + 1, y + 3);
    const headW = doc.getTextWidth(`${pw.peak_kw.toLocaleString()} kW peak`);
    font("normal", 9);
    color(MUTED);
    doc.text(`  ·  ${pw.idle_kw.toLocaleString()} kW idle`, MARGIN + 1 + headW, y + 3);
    y += 7;
    font("normal", 7.5);
    color(GRAY);
    doc.text("Idle", MARGIN + CONTENT_W - 26, y + 3, { align: "right" });
    doc.text("Peak", MARGIN + CONTENT_W - 1, y + 3, { align: "right" });
    y += 4;
    for (const item of pw.items) {
      ensure(9);
      stroke(RULE);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
      font("bold", 8.5);
      color(INK);
      doc.text(item.label, MARGIN + 1, y + 4);
      font("normal", 7.5);
      color(MUTED);
      doc.text(`${item.count} x ${item.model}`, MARGIN + 1, y + 7.4);
      font("normal", 8.5);
      doc.text(kw(item.idle_w), MARGIN + CONTENT_W - 26, y + 4, { align: "right" });
      font("bold", 8.5);
      color(INK);
      doc.text(kw(item.peak_w), MARGIN + CONTENT_W - 1, y + 4, { align: "right" });
      y += 9.5;
    }
    font("normal", 7.5);
    color(GRAY);
    for (const note of pw.notes) {
      const lines = wrap(note, CONTENT_W - 2);
      ensure(lines.length * 3.4 + 1);
      doc.text(lines, MARGIN + 1, y + 3);
      y += lines.length * 3.4 + 0.6;
    }
    y += 8;
  }

  // ---------------- Performance Metrics ----------------
  sectionTitle("Performance Metrics", 32);
  kvRows([
    ["Time to First Token (TTFT)", approxTime(perf.prefill_time)],
    ["Intertoken Latency (ITL)", approxTime(perf.generation_time)],
    ["Request Latency (E2E Latency)", e2e, true],
    ["Max Concurrent Users", String(perf.max_conc_users)],
    ...(perf.activeRequests != null
      ? [["Requests in flight", `~${perf.activeRequests}` + (inp.requestsPerUserPerMinute
          ? ` (${inp.concurrentUsers} users x ${inp.requestsPerUserPerMinute} requests/min)`
          : " (every user waiting at once)")] as [string, string]]
      : []),
  ]);
  const moeNote = lowConfidenceNote(result);
  if (moeNote) {
    font("normal", 7.5);
    const lines = wrap(moeNote, CONTENT_W - 2);
    ensure(lines.length * 3.4 + 2);
    doc.setTextColor(180, 83, 9);
    doc.text(lines, MARGIN + 1, y + 3.5);
    y += lines.length * 3.4 + 1.5;
  }
  y += 8;

  // ---------------- System & CPU Requirements ----------------
  sectionTitle("System & CPU Requirements", 36);
  {
    const mem = result.memoryRequirements;
    const cpu = result.cpuRequirements;
    const perNode = (each: number | undefined, total: number, unit: string) =>
      each && (cpu.nodes ?? 1) > 1 ? `${each.toLocaleString()} ${unit}/node (${total.toLocaleString()} ${unit} total)` : `${total} ${unit}`;
    const colW = (CONTENT_W - 8) / 2;
    ensure(4 * (LINE + 2.6) + 8);
    const top = y;
    font("bold", 9.5);
    color(INK);
    doc.text("Memory Requirements", MARGIN + 1, y + 3);
    doc.text("CPU Requirements", MARGIN + colW + 8 + 1, y + 3);
    y += 5;
    kvRows(
      [
        ["Model Memory", `${mem.model} GB`],
        ["RAG Memory", `${mem.rag} GB`],
        ["Total Required", `${mem.total} GB`, true],
      ],
      MARGIN, colW, 36,
    );
    const leftEnd = y;
    y = top + 5;
    kvRows(
      [
        ["CPU Cores", perNode(cpu.coresPerNode, cpu.cores, "cores")],
        ["System RAM", perNode(cpu.ramPerNode, cpu.ram, "GB")],
        ["Storage", cpu.storage],
      ],
      MARGIN + colW + 8, colW, 36,
    );
    y = Math.max(leftEnd, y) + 10;
  }

  // ---------------- Pre-quote Checks (Lenovo sizing rubric) ----------------
  {
    const checks = [...(result.checks ?? [])].sort((a, b) => (a.level === b.level ? 0 : a.level === "warning" ? -1 : 1));
    if (checks.length) {
      sectionTitle("Pre-quote Checks", 20);
      for (const c of checks) {
        font("normal", 8.5);
        const lines = wrap(c.text, CONTENT_W - 22);
        const h = lines.length * 4 + 3;
        ensure(h);
        const warn = c.level === "warning";
        font("bold", 7);
        if (warn) doc.setTextColor(180, 83, 9);
        else color(BLUE);
        doc.text(warn ? "WARNING" : "NOTE", MARGIN + 1, y + 3.6);
        font("normal", 8.5);
        color(INK);
        doc.text(lines, MARGIN + 20, y + 3.6);
        y += h;
      }
      y += 8;
    }
  }

  // ---------------- Disclaimer ----------------
  {
    font("normal", 8);
    const lines = wrap(DISCLAIMER, CONTENT_W);
    ensure(lines.length * 3.8 + 8);
    font("bold", 8);
    color(GRAY);
    doc.text("Disclaimer", MARGIN, y);
    font("normal", 8);
    doc.text(lines, MARGIN, y + 4.5);
  }

  // ---------------- Footer on every page ----------------
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    stroke(RULE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, PAGE_H - 13, PAGE_W - MARGIN, PAGE_H - 13);
    font("normal", 7.5);
    color(GRAY);
    doc.text("Lenovo LLM Sizing Calculator", MARGIN, PAGE_H - 8);
    doc.text("Estimates only - not a performance guarantee", PAGE_W / 2, PAGE_H - 8, { align: "center" });
    doc.text(`Page ${p} of ${pageCount}`, PAGE_W - MARGIN, PAGE_H - 8, { align: "right" });
  }

  return doc;
}
